import { forwardRef } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { SaveRegular, ArrowRightRegular } from '@fluentui/react-icons';
import { BasicButton } from '@components/common/BasicButton';

export interface InlineStepActionsProps {
  visible: boolean;
  continueDisabled?: boolean;
  onContinue: () => void;
  onSaveAsDraft: () => void;
  saveAsDraftDisabled?: boolean;
  continueLabel: string;
  saveAsDraftLabel: string;
  className?: string;
}

const useInlineStepActionsStyles = makeStyles({
  wrapper: {
    display: 'grid',
    gridTemplateRows: '0fr',
    transitionProperty: 'grid-template-rows',
    transitionDuration: '200ms',
    transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0ms',
    },
  },
  wrapperVisible: {
    gridTemplateRows: '1fr',
  },
  inner: {
    overflow: 'hidden',
    minHeight: '0',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
  },
});

export const InlineStepActions = forwardRef<HTMLDivElement, InlineStepActionsProps>(
  (
    {
      visible,
      continueDisabled = false,
      onContinue,
      onSaveAsDraft,
      saveAsDraftDisabled = false,
      continueLabel,
      saveAsDraftLabel,
      className,
    },
    ref
  ) => {
    const styles = useInlineStepActionsStyles();
    return (
      <div
        ref={ref}
        data-testid="inline-step-actions"
        className={mergeClasses(styles.wrapper, visible && styles.wrapperVisible, className)}
        aria-hidden={!visible}
      >
        <div className={styles.inner}>
          <div className={styles.actions}>
            <BasicButton
              variant="outline"
              size="medium"
              icon={<SaveRegular />}
              onClick={onSaveAsDraft}
              disabled={!visible || saveAsDraftDisabled}
            >
              {saveAsDraftLabel}
            </BasicButton>
            <BasicButton
              variant="primary"
              size="medium"
              iconPosition="after"
              icon={<ArrowRightRegular />}
              onClick={onContinue}
              disabled={!visible || continueDisabled}
            >
              {continueLabel}
            </BasicButton>
          </div>
        </div>
      </div>
    );
  }
);

InlineStepActions.displayName = 'InlineStepActions';
